import { useCallback, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import Clickable from 'components/Common/Clickable'

interface Props {
  value: string
  onCopyClick?: (event: React.MouseEvent<HTMLElement>) => void
  ariaLabel?: string
  /**
   * Prevent a click on this icon from triggering parent click handlers/links.
   * Useful when rendered inside a clickable tile/link.
   */
  stopPropagationOnCopy?: boolean
}

function CopyToClipboardClickableIcon(props: Props) {
  const { value, onCopyClick, ariaLabel, stopPropagationOnCopy } = props
  const intl = useIntl()

  const defaultAriaLabel = intl.formatMessage({
    id: 'OmhFnzHVTqf_B4xpe5PA',
    defaultMessage: 'Copy to clipboard',
    description: 'Default aria label for copy to clipboard button on the checkout confirmation page',
  })

  const [hasCopied, setCopied] = useState<boolean>(false)

  const copyToClipboard = useCallback(async(event?: React.MouseEvent<HTMLElement>) => {
    if (hasCopied) {
      return
    }

    if (event) {
      if (stopPropagationOnCopy) {
        event.preventDefault()
        event.stopPropagation()
      }

      if (onCopyClick) {
        onCopyClick(event)
      }
    }

    await copyValueToClipboard(value)

    setCopied(true)
    window.setTimeout(() => {
      setCopied(false)
    }, 1000)
  }, [hasCopied, onCopyClick, stopPropagationOnCopy, value])

  return (
    <Clickable onClick={copyToClipboard} disabled={hasCopied} aria-label={ariaLabel ?? defaultAriaLabel} className="no-print">
      {hasCopied ? <LineCheckIcon size="XS" /> : <LineCopyIcon size="XS" />}
    </Clickable>
  )
}

export default CopyToClipboardClickableIcon
